import { ReactElement } from "react";
import {
	DateString,
	IconAndText,
	ListItem,
	Space,
	PipeSeparator
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import { HubThumbnail } from "client/Features/Hub/HubThumbnail/HubThumbnail";
import styles from "./HubListItem.module.less";
import { HubItemMediaPreviewViewModel, ReactionCounterModel } from "client/Api/Hub/common";
import { NumberFormatter } from "client/Hooks";
import { useHubStatisticsFormatter } from "client/Features/Hub/useHubStatisticsFormatter";
import { TimeZone } from "client/DateAndTime/timezones";
import { HubSubtitleWithCategories } from "client/Features/Hub/HubSubtitleWithCategories";
import { ReactionStatistic, useReactionStatistic } from "client/Features/Reactions/useReactionStatistic";

export type HubListItemProps = {
	title: string;
	media: HubItemMediaPreviewViewModel;
	publicationDate: Date;
	categoryNames: readonly string[];
	numberOfComments: number;
	reactionCounters: readonly ReactionCounterModel[];
	authorName: string | null;
	areCommentsEnabled: boolean;
	isPinned: boolean;
	onNavigate(): void;
	trackingId: string;
	timezone: TimeZone;
	isNew: boolean;
};

export const HubListItem = (props: HubListItemProps): ReactElement => {
	const {
		title,
		media,
		categoryNames,
		publicationDate,
		numberOfComments,
		reactionCounters,
		authorName,
		areCommentsEnabled,
		isPinned,
		onNavigate,
		trackingId,
		isNew,
		timezone
	} = props;

	const statisticsFormatter: NumberFormatter = useHubStatisticsFormatter();
	const reactionStatistic: ReactionStatistic = useReactionStatistic(reactionCounters);

	return (
		<ListItem
			trackingId={trackingId}
			hasStatusDot={isNew}
		>
			<ListItem.Content
				onClick={onNavigate}
				thumbnail={(
					<div className={styles.thumbnail}>
						<HubThumbnail
							title={title}
							media={media}
							hubItemKind={HubItemKind.Announcement}
							mediaPlaceholderSize="small"
						/>
					</div>
				)}
			>
				<ListItem.Row>
					{isPinned && (
						<ListItem.TitleIcon kind="thumbtack" color="primary" />
					)}
					<ListItem.Title>
						{title}
					</ListItem.Title>
				</ListItem.Row>
				<ListItem.SubTitle>
					<HubSubtitleWithCategories
						categoryNames={categoryNames}
					>
						<>
							<DateString
								value={publicationDate}
								format="RelativeTimestamp"
								timezone={timezone}
							/>
							{authorName && (
								<>
									<PipeSeparator />
									{localize("_(Edited)By")}
									<Space />
									{authorName}
								</>
							)}
						</>
					</HubSubtitleWithCategories>
				</ListItem.SubTitle>
				<div className={styles.stats}>
					<span className={styles.likeStatContainer}>
						<IconAndText
							text={statisticsFormatter(reactionStatistic.total)}
						>
							<IconAndText.Icon
								value={reactionStatistic.icon}
							/>
						</IconAndText>
					</span>
					{areCommentsEnabled && (
						<span>
							<IconAndText
								text={statisticsFormatter(numberOfComments)}
							>
								<IconAndText.Icon
									value="comment"
								/>
							</IconAndText>
						</span>
					)}
				</div>
			</ListItem.Content>
		</ListItem>
	);
};

HubListItem.displayName = "HubListItem";
